import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  DeleteRouteDeviation,
  GetRouteDeviation,
  GetRouteDeviationCandidates,
  SaveRouteDeviation,
} from '@/apis/shipment';
import type { SaveRouteDeviationPayload } from '@/apis/types';
import { useToken } from '@/common/helper';
import { isSearchInProgress } from './routeDeviationErrors';

export const DEFAULT_MILES_OFF = 3;
// Routes are grouped no coarser than this, so miles above it never regroup them.
const MAX_GROUPING_MILES = 3;
// Below that, routes are grouped at the half mile at or below the tolerance, so
// miles typed to a tenth start at most five different route searches.
const GROUPING_STEP_MILES = 0.5;

/**
 * The distance the search groups routes at for a tolerance of `miles`:
 * min(3, miles), rounded down to a half mile (2.3 groups at 2.0). The server
 * uses the same rule.
 */
export const groupingMiles = (miles: number): number =>
  Math.min(
    MAX_GROUPING_MILES,
    Math.floor(miles / GROUPING_STEP_MILES + 1e-9) * GROUPING_STEP_MILES
  );

// The server already holds a busy search (429) for up to 20 seconds before
// answering, so it is asked again only twice, after 1 and 2 seconds, before the
// user is told. The delay before attempt n+1 is entry n.
const SEARCH_RETRY_DELAYS_MS = [1000, 2000];

export const routeDeviationKeys = {
  setting: (shipmentId: string) =>
    ['route-deviation', 'setting', shipmentId] as const,
  candidates: (shipmentId: string, milesOff: number) =>
    ['route-deviation', 'candidates', shipmentId, milesOff] as const,
};

const requireToken = (checkTokenExpiry: () => string | null): string => {
  const token = checkTokenExpiry();
  if (!token) throw new Error('No token available');
  return token;
};

/** The saved setting, or none. Read fresh each time the modal opens. */
export function useRouteDeviationSetting(shipmentId: string, enabled: boolean) {
  const { checkTokenExpiry } = useToken();
  return useQuery({
    queryKey: routeDeviationKeys.setting(shipmentId),
    queryFn: ({ signal }) =>
      GetRouteDeviation(requireToken(checkTokenExpiry), shipmentId, signal),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Route groups past deliveries took. The search is slow, so it is asked again
 * only when another search for the shipment is already running. A new grouping
 * distance keeps the previous groups on screen until its own arrive.
 */
export function useRouteDeviationCandidates(
  shipmentId: string,
  milesOff: number,
  enabled: boolean
) {
  const { checkTokenExpiry } = useToken();
  return useQuery({
    queryKey: routeDeviationKeys.candidates(shipmentId, milesOff),
    queryFn: ({ signal }) =>
      GetRouteDeviationCandidates(
        requireToken(checkTokenExpiry),
        shipmentId,
        milesOff,
        signal
      ),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: (failureCount, error) =>
      isSearchInProgress(error) && failureCount < SEARCH_RETRY_DELAYS_MS.length,
    retryDelay: (failureCount) => SEARCH_RETRY_DELAYS_MS[failureCount],
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

/** Create or replace the setting, then refresh the saved copy the next opening reads. */
export function useSaveRouteDeviation(shipmentId: string) {
  const { checkTokenExpiry } = useToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveRouteDeviationPayload) =>
      SaveRouteDeviation(requireToken(checkTokenExpiry), shipmentId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: routeDeviationKeys.setting(shipmentId),
      }),
  });
}

/** Delete the setting, then refresh the saved copy the next opening reads. */
export function useDeleteRouteDeviation(shipmentId: string) {
  const { checkTokenExpiry } = useToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      DeleteRouteDeviation(requireToken(checkTokenExpiry), shipmentId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: routeDeviationKeys.setting(shipmentId),
      }),
  });
}
